import { z } from 'zod';
import { escapeHtml, safeUrl } from '../../render/escape.js';
import type { DefModuleMountT } from '../../schema/split.js';
import { ModuleOpError, identityMigration, type FirstPartyModule, type ModuleOperation } from '../kernel.js';
import {
  FormsContactConfig,
  FormsContactData,
  type FormsContactConfigT,
  type FormsContactDataT,
} from './schema.js';

const copyFields = z.object({
  heading: z.string().min(1).max(120).optional(),
  intro: z.string().max(400).optional(),
  submitLabel: z.string().min(1).max(60).optional(),
  successMessage: z.string().min(1).max(240).optional(),
  errorMessage: z.string().min(1).max(240).optional(),
  consentText: z.string().max(400).optional(),
}).strict().refine((fields) => Object.keys(fields).length > 0, 'at least one field is required');
const updateCopyInput = z.object({ fields: copyFields }).strict();
const DEFAULT_CONFIG: FormsContactConfigT = { formId: 'contact-form', submitPath: '/api/forms/contact' };

const updateCopy: ModuleOperation<FormsContactDataT> = {
  input: updateCopyInput,
  describe: 'Update the bounded public copy of the contact form.',
  apply(data, input) {
    const parsed = updateCopyInput.safeParse(input);
    if (!parsed.success) {
      throw new ModuleOpError('invalid-input', parsed.error.issues[0]?.message ?? 'invalid operation input');
    }
    // Only keys the caller actually sent are present (zod drops absent
    // optionals), so a shallow assign patches exactly those fields.
    return Object.assign(structuredClone(data), parsed.data.fields);
  },
};

function renderForm(
  mount: DefModuleMountT,
  data: FormsContactDataT,
  config: FormsContactConfigT = DEFAULT_CONFIG,
): { html: string } {
  const parsedConfig = FormsContactConfig.parse(config);
  const anchor = escapeHtml(mount.anchor);
  const parts = [
    `<section id="${anchor}" class="ve-module ve-forms-contact">`,
    `  <h2 class="ve-forms-contact__heading">${escapeHtml(data.heading)}</h2>`,
  ];
  if (data.intro) parts.push(`  <p class="ve-forms-contact__intro">${escapeHtml(data.intro)}</p>`);
  parts.push(
    `  <form class="ve-forms-contact__form" method="post" action="${safeUrl(parsedConfig.submitPath)}">`,
    `    <input type="hidden" name="formId" value="${escapeHtml(parsedConfig.formId)}">`,
    `    <label for="${anchor}-name">Name</label>`,
    `    <input id="${anchor}-name" name="name" type="text" maxlength="120" autocomplete="name" required>`,
    `    <label for="${anchor}-email">Email</label>`,
    `    <input id="${anchor}-email" name="email" type="email" maxlength="254" autocomplete="email" required>`,
    `    <label for="${anchor}-message">Message</label>`,
    `    <textarea id="${anchor}-message" name="message" maxlength="4000" rows="6" required></textarea>`,
    '    <div hidden aria-hidden="true">',
    `      <label for="${anchor}-website">Website</label>`,
    `      <input id="${anchor}-website" name="website" type="text" tabindex="-1" autocomplete="off">`,
    '    </div>',
  );
  if (data.consentText) parts.push(`    <p class="ve-forms-contact__consent">${escapeHtml(data.consentText)}</p>`);
  parts.push(
    `    <button type="submit">${escapeHtml(data.submitLabel)}</button>`,
    '  </form>',
    `  <p class="ve-forms-contact__status ve-forms-contact__status--success" hidden>${escapeHtml(data.successMessage)}</p>`,
    `  <p class="ve-forms-contact__status ve-forms-contact__status--error" hidden>${escapeHtml(data.errorMessage)}</p>`,
    '</section>',
  );
  return { html: parts.join('\n') };
}

const migrate = identityMigration('forms.contact', FormsContactData);

// Server-side-only per-instance config (SiteModulesRepository / D7 Unit B):
// recipient/origins/Turnstile/retention never enter module data (see
// schema.ts's header) — this is the schema the runtime validates that
// private config against when a module instance is enabled.
const privateConfig = z.object({
  recipientEmail: z.string().email(),
  allowedOrigins: z.array(z.string()).default([]),
  turnstileSecretRef: z.string().optional(),
  retentionDays: z.number().int().positive().max(3650).optional(),
}).strict();

export const formsContact: FirstPartyModule<FormsContactDataT, FormsContactConfigT> = {
  id: 'forms.contact',
  contractVersion: 1,
  dataVersion: 1,
  dataSchema: FormsContactData,
  configSchema: FormsContactConfig,
  privateConfigSchema: privateConfig,
  operations: { updateCopy },
  views: { form: { render: renderForm } },
  migrate,
};
